// websocket.js
import { useEffect, useRef, useCallback } from 'react';

export const useWebSocket = (wsUrl, onMessageReceived, onReconnect) => {
    const ws = useRef(null);
    const reconnectTimeout = useRef(null);
    const isConnecting = useRef(false);
    // True after a connection closed while the hook is still mounted, so the
    // next successful open is a reconnect. Kept in a ref (like onReconnect)
    // so it never re-triggers the connect effect.
    const hadDisconnect = useRef(false);
    const onReconnectRef = useRef(onReconnect);
    onReconnectRef.current = onReconnect;

    const connectWebSocket = useCallback(() => {
        if (isConnecting.current || (ws.current?.readyState === WebSocket.OPEN)) {
            return;
        }

        try {
            isConnecting.current = true;

            if (ws.current) {
                ws.current.close();
            }

            // Create WebSocket only once
            ws.current = new WebSocket(wsUrl);
            console.log('Attempting to connect to:', wsUrl);

            ws.current.onopen = () => {
                console.log('WebSocket Connected');
                isConnecting.current = false;
                if (hadDisconnect.current) {
                    hadDisconnect.current = false;
                    // Messages published while disconnected are lost for good
                    // (the server relay has no replay), so let the consumer
                    // resync its realtime-fed state from the server.
                    onReconnectRef.current?.();
                }
            };

            ws.current.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    onMessageReceived(data, wsUrl);
                } catch (error) {
                    console.error('Error processing message:', error);
                }
            };

            ws.current.onerror = (error) => {
                console.error('WebSocket Error:', error);
                isConnecting.current = false;
            };

            ws.current.onclose = (event) => {
                console.log('WebSocket Closed. Code:', event.code, 'Reason:', event.reason);
                isConnecting.current = false;

                // Only reconnect if we're not unmounting
                if (ws.current) {
                    hadDisconnect.current = true;
                    reconnectTimeout.current = setTimeout(connectWebSocket, 3000);
                }
            };

        } catch (error) {
            console.error('Error in connectWebSocket:', error);
            isConnecting.current = false;
            if (ws.current) {
                reconnectTimeout.current = setTimeout(connectWebSocket, 3000);
            }
        }
    }, [wsUrl]);

    useEffect(() => {
        console.log('useWebSocket effect running');
        connectWebSocket();

        return () => {
            console.log('Cleaning up WebSocket connection');
            if (reconnectTimeout.current) {
                clearTimeout(reconnectTimeout.current);
                reconnectTimeout.current = null;
            }
            if (ws.current) {
                ws.current.onopen = null;
                ws.current.onmessage = null;
                ws.current.onerror = null;
                ws.current.onclose = null;
                
                ws.current.close();
                ws.current = null;
                isConnecting.current = false;
            }
        };
    }, [connectWebSocket]);

    return ws.current;
};
